/* public-i18n-migrated */
import { useI18n } from "@/i18n";
import { PublicLanguageSelect } from '@/components/PublicLanguageSelect';
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Star, X, ChevronLeft, ChevronRight } from "lucide-react";
import AllRounderLogo from "@/components/AllRounderLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso, lang) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return "";
  }
};

function Stars({ value = 0, size = 14 }) {
  const { tx } = useI18n();

  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          className={n <= value ? "text-[#E85A2C] fill-[#E85A2C]" : "text-white/15"}
          style={{ width: size, height: size }}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

export default function BbqReviewsPage() {
  const { tx, lang } = useI18n();

  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  useEffect(() => {
    document.title = "Event Reviews · South Bank BBQ";
    axios
      .get(`${API}/bbq-reviews`)
      .then((r) => setReviews(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const allPhotos = reviews.flatMap((r) =>
    (r.photos || []).map((src) => ({ src, name: r.name, rating: r.rating, text: r.text }))
  );

  const avg = reviews.length > 0
    ? (reviews.reduce((s, r) => s + Number(r.rating || 0), 0) / reviews.length).toFixed(1)
    : "—";

  const openLightbox = (photos, index) => setLightbox({ photos, index });
  const closeLightbox = () => setLightbox(null);
  const nextLb = () => setLightbox((lb) => lb && { ...lb, index: (lb.index + 1) % lb.photos.length });
  const prevLb = () => setLightbox((lb) => lb && { ...lb, index: (lb.index - 1 + lb.photos.length) % lb.photos.length });

  return (
    <main data-testid="bbq-reviews-page" className="min-h-screen bg-[#0A0806] text-white">
      {/* Nav */}
      <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-[#0A0806]/70 border-b border-white/5">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-5 sm:px-10 py-4">
          <Link to="/bbq" className="flex items-center gap-3 group" data-testid="reviews-back-btn">
            <ArrowLeft className="w-4 h-4 text-white/60 group-hover:text-[#E85A2C] transition-colors" />
            <AllRounderLogo variant="horizontal" color="#FAFAFA" />
          </Link>
          <PublicLanguageSelect id="reviews-language"/>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-5 sm:px-10 pt-32 pb-24">
        {/* Header */}
        <div className="mb-12">
          <p className="text-[11px] uppercase tracking-[0.32em] font-bold text-[#E85A2C]">{tx("// Event Reviews · 이벤트 후기")}</p>
          <h1
            className="mt-4 text-white text-5xl sm:text-7xl font-bold tracking-tight leading-[0.95]"
            style={{ fontFamily: "'Fraunces', serif", fontWeight: 600 }}
          > {tx("Every message. Every photo.")} </h1>
          <div className="mt-8 flex items-center gap-6 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">{tx("Average")}</div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-4xl font-bold text-[#E85A2C]" style={{ fontFamily: "'Fraunces', serif" }} data-testid="reviews-avg">{avg}</span>
                <span className="text-white/40 text-sm">/ 5.0</span>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">{tx("Reviews")}</div>
              <div className="text-4xl font-bold text-white mt-1" style={{ fontFamily: "'Fraunces', serif" }} data-testid="reviews-count">{reviews.length}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-white/50">{tx("Photos")}</div>
              <div className="text-4xl font-bold text-white mt-1" style={{ fontFamily: "'Fraunces', serif" }} data-testid="reviews-photo-count">{allPhotos.length}</div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-white/40"><Loader2 className="w-6 h-6 animate-spin inline-block" /></div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-20 text-white/40 border border-white/10 rounded-sm"> {tx("No reviews yet —")} <Link to="/bbq#reviews" className="text-[#E85A2C] underline">{tx("be the first")}</Link>.
          </div>
        ) : (
          <>
            {/* Media Gallery */}
            {allPhotos.length > 0 && (
              <section className="mb-16">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-[11px] uppercase tracking-[0.28em] font-bold text-white/70">{tx("Media Gallery")}</h2>
                  <span className="text-[11px] text-white/40">{allPhotos.length} {tx("photos")}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="reviews-media-grid">
                  {allPhotos.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => openLightbox(allPhotos, i)}
                      className="aspect-square rounded-sm overflow-hidden border border-white/10 hover:border-[#E85A2C] transition-colors group"
                    >
                      <img src={p.src} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
                    </button>
                  ))}
                </div>
              </section>
            )}

            {/* Reviews */}
            <section>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-[11px] uppercase tracking-[0.28em] font-bold text-white/70">{tx("All Reviews")}</h2>
              </div>
              <div className="border border-white/10 rounded-sm bg-[#0F0B08] overflow-hidden divide-y divide-white/5" data-testid="reviews-list">
                {reviews.map((r) => (
                  <div
                    key={r.id}
                    className="flex items-center gap-4 px-4 sm:px-6 py-4 hover:bg-white/[0.03] transition-colors"
                    data-testid={`reviews-page-card-${r.id}`}
                  >
                    <div className="w-24 sm:w-32 shrink-0 text-white font-bold truncate" style={{ fontFamily: "'Fraunces', serif" }}>
                      {r.name}
                    </div>
                    <div className="shrink-0"><Stars value={r.rating} size={14} /></div>
                    <p className="flex-1 min-w-0 text-white/70 text-sm truncate">{r.text}</p>
                    {r.photos?.length > 0 && (
                      <span className="hidden sm:inline shrink-0 text-[10px] uppercase tracking-widest text-[#E85A2C] font-bold">
                        {r.photos.length} 📷
                      </span>
                    )}
                    <span className="shrink-0 hidden md:inline text-white/40 text-[10px] uppercase tracking-widest whitespace-nowrap">
                      {fmtDate(r.created_at,lang)}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/95 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={closeLightbox}
          data-testid="reviews-lightbox"
        >
          <button
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-6 right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
            aria-label={tx("close")}
          >
            <X className="w-5 h-5" />
          </button>
          {lightbox.photos.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevLb(); }}
                className="absolute left-4 sm:left-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label={tx("prev")}
              >
                <ChevronLeft className="w-6 h-6" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextLb(); }}
                className="absolute right-4 sm:right-8 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white transition-colors"
                aria-label={tx("next")}
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </>
          )}
          <div className="max-w-5xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={lightbox.photos[lightbox.index].src}
              alt=""
              className="max-h-[75vh] w-auto object-contain rounded-sm border border-white/10"
            />
            <div className="mt-4 text-center max-w-xl">
              <div className="flex items-center justify-center gap-3 mb-1">
                <span className="text-white font-bold" style={{ fontFamily: "'Fraunces', serif" }}>{lightbox.photos[lightbox.index].name}</span>
                <Stars value={lightbox.photos[lightbox.index].rating} size={14} />
              </div>
              <p className="text-white/60 text-sm leading-relaxed">{lightbox.photos[lightbox.index].text}</p>
              <div className="mt-2 text-white/30 text-[10px] uppercase tracking-widest">
                {lightbox.index + 1} / {lightbox.photos.length}
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
